import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Heart, Star, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

function Stars({ value = 0, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`${size} ${s <= Math.round(value) ? "fill-[#ffd600] text-[#ffd600]" : "text-[#d4d4d4]"}`} strokeWidth={1.5} />
      ))}
    </span>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setQty(r.data.min_qty || 1);
    }).catch(() => setP(false));
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data.items)).catch(() => {});
  };
  useEffect(load, [id]);

  if (p === null) return <div className="max-w-7xl mx-auto px-6 py-24 animate-pulse">Caricamento...</div>;
  if (p === false)
    return (
      <div data-testid="product-not-found" className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl uppercase">Prodotto non trovato</h1>
        <Link to="/catalogo" className="btn-hard mt-6 inline-flex">Torna al catalogo</Link>
      </div>
    );

  const addToCart = () => {
    add(p, qty);
    toast.success("Aggiunto al carrello");
  };

  const toggleWish = async () => {
    try {
      const { data } = await api.post(`/wishlist/${p.id}`);
      toast.success(data.wishlisted ? "Aggiunto alla wishlist" : "Rimosso dalla wishlist");
    } catch {
      toast.error("Accedi per usare la wishlist");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/products/${p.id}/reviews`, { rating, comment });
      toast.success("Recensione pubblicata");
      setComment("");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        <Link to="/catalogo" className="hover:text-[#ff4500] transition-colors">Catalogo</Link>
        <span className="mx-2">/</span>
        <Link to={`/catalogo?categoria=${encodeURIComponent(p.category)}`} className="hover:text-[#ff4500] transition-colors">{p.category}</Link>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:sticky lg:top-40 self-start">
          <div className="relative">
            <div className="absolute -top-3 -left-3 w-full h-full border-2 border-[#0a0a0a]" />
            <img data-testid="product-image" src={p.image} alt={p.name} className="relative w-full aspect-square object-cover bg-[#f4f4f0]" />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <div className="overline-tag">{p.brand}</div>
          <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-3xl md:text-5xl leading-[1.02]">{p.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            {p.rating_avg ? (
              <>
                <Stars value={p.rating_avg} />
                <span className="text-sm text-muted-foreground">{p.rating_avg} · {p.review_count} recensioni</span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Nessuna recensione ancora</span>
            )}
          </div>

          <div data-testid="product-price" className="mt-6 font-display font-semibold text-4xl text-[#ff4500]">{eur(p.price)}</div>
          <div className="mt-1 text-sm">
            {p.stock >= 2 ? (
              <span data-testid="product-stock" className="text-[#10b981] font-semibold">Disponibile · {p.stock} in magazzino</span>
            ) : (
              <span data-testid="product-stock" className="text-destructive font-semibold">Esaurito · in attesa di rifornimento</span>
            )}
          </div>

          <p className="mt-6 text-muted-foreground leading-relaxed">{p.description}</p>

          {p.specs && Object.keys(p.specs).length > 0 && (
            <div className="mt-8">
              <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Specifiche tecniche</div>
              <Table data-testid="product-specs-table">
                <TableBody>
                  {Object.entries(p.specs).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell className="font-semibold w-40 uppercase text-xs tracking-wide">{k}</TableCell>
                      <TableCell className="font-mono text-sm">{v}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-8 flex items-stretch gap-3 flex-wrap">
            <div className="flex items-center border-2 border-[#0a0a0a]">
              <button data-testid="qty-minus-button" onClick={() => setQty(Math.max(p.min_qty || 1, qty - 1))} className="px-3 py-3 hover:bg-[#f4f4f0] transition-colors" aria-label="Diminuisci">
                <Minus className="w-4 h-4" />
              </button>
              <span data-testid="qty-value" className="w-10 text-center font-mono">{qty}</span>
              <button data-testid="qty-plus-button" onClick={() => setQty(Math.min(p.max_qty > 0 ? Math.min(p.max_qty, p.stock || p.max_qty) : (p.stock || 99), qty + 1))} className="px-3 py-3 hover:bg-[#f4f4f0] transition-colors" aria-label="Aumenta">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button data-testid="add-to-cart-button" onClick={addToCart} disabled={p.stock < 2} className="btn-hard flex-1 min-w-[200px]">
              <ShoppingCart className="w-4 h-4" strokeWidth={2} /> {p.stock < 2 ? "Esaurito" : "Aggiungi al carrello"}
            </button>
            <button data-testid="wishlist-button" onClick={toggleWish} aria-label="Wishlist" className="btn-outline-hard !px-4">
              <Heart className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          {((p.min_qty || 1) > 1 || (p.max_qty || 0) > 0) && (
            <p data-testid="qty-rules-notice" className="mt-4 text-sm bg-[#ffd600]/30 border border-[#ffd600] p-3">
              {(p.min_qty || 1) > 1 && <>Per questo articolo devi acquistare <strong>minimo {p.min_qty} pezzi</strong>. </>}
              {(p.max_qty || 0) > 0 && <>Puoi acquistarne <strong>massimo {p.max_qty} pezzi</strong> per ordine.</>}
            </p>
          )}
        </motion.div>
      </div>

      {/* REVIEWS */}
      <section data-testid="reviews-section" className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="overline-tag">Opinioni verificate</div>
          <h2 className="mt-3 font-display font-medium uppercase tracking-tight text-3xl md:text-4xl">Recensioni ({reviews.length})</h2>
          <div className="mt-8 flex flex-col gap-6">
            {reviews.length === 0 && <p className="text-muted-foreground text-sm">Ancora nessuna recensione. Sii il primo a condividere la tua esperienza.</p>}
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="border border-[#e5e5e5] bg-white p-5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{r.user_name}</span>
                  <Stars value={r.rating} />
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.comment}</p>
                <div className="mt-2 text-xs text-muted-foreground font-mono">{new Date(r.created_at).toLocaleDateString("it-IT")}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="bg-white border border-[#e5e5e5] p-6 lg:sticky lg:top-40">
            <h3 className="font-display font-medium text-xl uppercase tracking-tight">Scrivi una recensione</h3>
            {user ? (
              <form onSubmit={submitReview} className="mt-5 flex flex-col gap-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} type="button" data-testid={`rating-star-${s}`} onClick={() => setRating(s)} aria-label={`${s} stelle`}>
                      <Star className={`w-7 h-7 ${s <= rating ? "fill-[#ffd600] text-[#ffd600]" : "text-[#d4d4d4]"}`} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
                <Textarea
                  data-testid="review-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Come ti sei trovato con questo prodotto?"
                  className="rounded-none border-2 border-[#0a0a0a] min-h-[110px]"
                />
                <button data-testid="review-submit-button" disabled={sending} className="btn-dark w-full">
                  {sending ? "Invio..." : "Pubblica recensione"}
                </button>
              </form>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                <Link data-testid="review-login-link" to="/accedi" className="text-[#ff4500] font-semibold hover:underline">Accedi</Link> per lasciare una recensione.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
